import React, {
  useEffect,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

import { useForm, useGeolocation } from '@hooks';
import { renderSomethingWentWrong } from '@utils';
import { useErrorReporter } from '@lib/errorReporter';
import { useAuth } from '@lib/authentication';
import { useConnectionChange } from '@lib/connectionChange';
import { useInsightsService } from '@lib/insightsService';
import { useProfile } from '@lib/profile';
import { VPN_OR_PROXY_LOGIN_ERROR } from '@constants';
import {
  AuthenticationAttemptsLimitReached,
  DistanceExceededFromLastConnection,
  InvalidCredentials,
  InvestorHasAlreadyAnActiveSession,
  UnusualAccess
} from '@lib/hundredBricksAPI/authentication';

import ActionText from '../ActionText';
import Button from '../Button';
import Form from '../Form';
import Modal from '../Modal';
import PasswordField from '../PasswordField';
import toast from '../toast';

import RenewSessionMessage from './RenewSessionMessage';
import RenewSessionOptions from './RenewSessionOptions';
import {
  ModalTitle,
  ButtonContainer,
  LinksContainer,
  VPNErrorContainer,
  OptionContainer,
  ModalMessageContainer
} from './RenewSessionModal.style';

const passwordSchema = {
  password: {
    required: true
  }
};

const PASSWORD_MINIMUM_LENGTH = 6;
const INCORRECT_PASSWORD_MESSAGE = '¡Vaya! La contraseña que escribiste no es correcta.'
  + ' Intenta de nuevo.';
const ERROR_LOCATION_BLOCKED = 'Ubicación desactivada. Activa los permisos de ubicación '
  + 'en tu navegador para acceder a tu cuenta.';
const ERROR_LOCATION_TRANSIENT = 'No pudimos obtener tu ubicación. Revisa tu conexión y '
  + 'vuelve a intentarlo.';
const ERROR_LOCATION_BY_REASON = {
  unsupported: 'El navegador o la app que estás usando no permite compartir tu ubicación. '
    + 'Abre este enlace desde tu navegador principal.',
  positionUnavailable: 'Tu dispositivo tiene el GPS o los servicios de ubicación desactivados. '
    + 'Actívalos y vuelve a intentarlo.',
  timeout: ERROR_LOCATION_TRANSIENT,
  unavailable: ERROR_LOCATION_TRANSIENT
};

const RenewSessionModal = ({
  isOpen,
  onClose,
  title,
  description
}) => {
  const navigate = useNavigate();
  const form = useForm({ initialValues: { password: '' }, schema: passwordSchema });
  const errorReporter = useErrorReporter();
  const { authenticationEvent, reportInsight } = useInsightsService();
  const { getCurrentPosition, isPermissionsApiSupported } = useGeolocation();
  const { profile, isLoading: isLoadingProfile } = useProfile();
  const { hasConnectionChangeError, setConnectionChangeData } = useConnectionChange();

  const {
    renewSession,
    error: loginError,
    isLoading: isAuthenticating,
    logout,
    clearSession,
    token
  } = useAuth();

  const email = profile.email.address;
  const [tokenValue] = useState(() => token?.value);

  const onSubmit = (latitude, longitude) => {
    renewSession(email, form.values.password, latitude, longitude);
  };

  const handleSubmit = async () => {
    // This validation is temporary; once the backend implements the error code to handle this case
    if (String(form.values.password).length < PASSWORD_MINIMUM_LENGTH) {
      form.addError({
        field: 'password',
        message: INCORRECT_PASSWORD_MESSAGE
      });

      return;
    }

    getCurrentPosition(coords => {
      const { latitude, longitude } = coords;

      onSubmit(latitude, longitude);
    }, reason => {
      const effectiveReason = isPermissionsApiSupported === false ? 'unsupported' : reason;

      toast.danger(ERROR_LOCATION_BY_REASON[effectiveReason] ?? ERROR_LOCATION_BLOCKED);
    });
  };

  const handleForgotPassword = () => {
    clearSession();
    navigate('/forgot-password');
  };

  const handleUnusualAccessUnlock = () => {
    clearSession();
    navigate(`/unusual-access-unblock?email=${encodeURIComponent(email)}`);
  };

  const handleLoginError = error => {
    reportInsight(authenticationEvent.logInError, error);

    if (error instanceof InvalidCredentials) {
      form.addError({
        field: 'password',
        message: INCORRECT_PASSWORD_MESSAGE
      });
    } else {
      errorReporter.critical(error);
      renderSomethingWentWrong();
    }
  };

  const hasModalError = loginError instanceof AuthenticationAttemptsLimitReached
    || loginError instanceof InvestorHasAlreadyAnActiveSession
    || loginError instanceof UnusualAccess;

  const onCloseRef = useRef(onClose);
  const formRef = useRef(form);
  const connectionChangeRef = useRef({ hasConnectionChangeError, setConnectionChangeData });
  const handleLoginErrorRef = useRef(handleLoginError);

  useEffect(() => {
    onCloseRef.current = onClose;
    formRef.current = form;
    connectionChangeRef.current = { hasConnectionChangeError, setConnectionChangeData };
    handleLoginErrorRef.current = handleLoginError;
  });

  useEffect(() => {
    if (token && tokenValue !== token.value) {
      onCloseRef.current();
      formRef.current.resetValues();

      const {
        hasConnectionChangeError: currentHasConnectionChangeError,
        setConnectionChangeData: currentSetConnectionChangeData
      } = connectionChangeRef.current;

      if (currentHasConnectionChangeError) {
        currentSetConnectionChangeData({ error: undefined, hasConnectionChangeError: false });
      }
    }
  }, [token, tokenValue]);

  useEffect(() => {
    if (loginError && !hasModalError) {
      handleLoginErrorRef.current(loginError);
    }
  }, [loginError, hasModalError]);

  return (
    <Modal isOpen={isOpen} preventClose>
      <ModalTitle>{title}</ModalTitle>

      {hasModalError
        ? (
          <ModalMessageContainer>
            <RenewSessionMessage error={loginError} />

            <OptionContainer>
              <RenewSessionOptions
                error={loginError}
                onConfirm={handleUnusualAccessUnlock}
                onCancel={logout}
              />
            </OptionContainer>
          </ModalMessageContainer>
        )
        : (
          <div>
            <Form onSubmit={form.handleSubmit(handleSubmit)}>
              <PasswordField
                id="password"
                focus
                label={description}
                placeholder="Escribe tu contraseña"
                value={form.values.password}
                {...form.fieldProps('password')}
              />

              {
                loginError instanceof DistanceExceededFromLastConnection && (
                  <VPNErrorContainer>
                    {VPN_OR_PROXY_LOGIN_ERROR}
                  </VPNErrorContainer>
                )
              }

              <ButtonContainer>
                <Button
                  type="submit"
                  isLoading={isAuthenticating || isLoadingProfile}
                >
                  Reactivar sesión
                </Button>
              </ButtonContainer>
            </Form>

            <LinksContainer>
              <ActionText
                color="info"
                onClick={handleForgotPassword}
                disabled={isAuthenticating || isLoadingProfile}
              >
                Restablece tu contraseña
              </ActionText>

              <ActionText
                color="info"
                onClick={logout}
                disabled={isAuthenticating || isLoadingProfile}
              >
                Cerrar sesión
              </ActionText>
            </LinksContainer>
          </div>
        )}
    </Modal>
  );
};

RenewSessionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired
};

export default RenewSessionModal;
